import { Wishlist } from '@theme/wishlist';
import { sectionRenderer, morphSection } from '@theme/section-renderer';

/**
 * Shows the item count and wires the "Clear" action once the real wishlist
 * count is known (the section's own Liquid render has no access to
 * localStorage, so the toolbar starts hidden with an empty count).
 * @param {HTMLElement} container
 * @param {number} count
 */
function updateToolbar(container, count) {
  const toolbar = container.querySelector('[data-wishlist-toolbar]');
  if (!(toolbar instanceof HTMLElement)) return;

  if (count === 0) {
    toolbar.hidden = true;
    return;
  }

  const countLabel = toolbar.querySelector('[data-wishlist-count]');
  if (countLabel) {
    countLabel.textContent = `${count} ${count === 1 ? 'item' : 'items'} in your wishlist`;
  }

  const clearButton = toolbar.querySelector('[data-wishlist-clear]');
  if (clearButton instanceof HTMLElement) {
    clearButton.addEventListener(
      'click',
      () => {
        Wishlist.clearProducts();
        location.reload();
      },
      { once: true }
    );
  }

  toolbar.hidden = false;
}

/**
 * Hydrates the wishlist results section with the customer's actual
 * wishlisted products, fetched by id through Shopify's native search
 * (the same technique `@theme/predictive-search` uses for "recently viewed").
 * The section's own Liquid render has no access to localStorage, so it
 * always renders its empty state first; this replaces it once the real
 * wishlist is known, client-side.
 */
function hydrateWishlistResults() {
  const container = document.querySelector('[data-wishlist-section-id]');
  if (!(container instanceof HTMLElement)) return;

  const sectionId = container.dataset.wishlistSectionId;
  if (!sectionId) return;

  const wishlisted = Wishlist.getProducts();
  if (wishlisted.length === 0) return;

  const url = new URL(Theme.routes.search_url, location.origin);
  url.searchParams.set('q', wishlisted.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');

  sectionRenderer.getSectionHTML(sectionId, false, url).then((html) => {
    if (!html) return;

    morphSection(sectionId, html).then(() => {
      const refreshedContainer = document.querySelector('[data-wishlist-section-id]');
      if (refreshedContainer instanceof HTMLElement) {
        updateToolbar(refreshedContainer, wishlisted.length);
      }
    });
  });
}

hydrateWishlistResults();
